import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

/**
 * Returns a styled Badge component for document statuses.
 */
export const getStatusBadge = (status) => {
  const map = {
    indexed: { color: 'bg-green-500/15 text-green-600 border-green-200', label: 'Indexiert' },
    processing: { color: 'bg-yellow-500/15 text-yellow-600 border-yellow-200', label: 'Verarbeitung' },
    failed: { color: 'bg-red-500/15 text-red-600 border-red-200', label: 'Fehler' },
    uploaded: { color: 'bg-blue-500/15 text-blue-600 border-blue-200', label: 'Hochgeladen' }
  };
  const s = map[status] || { color: 'bg-slate-100 text-slate-600', label: status };
  return <Badge className={`${s.color} hover:${s.color} border shadow-none font-medium`}>{s.label}</Badge>;
};

/**
 * Copy text to clipboard and show a toast notification.
 */
export const copyToClipboard = (text, label = 'Text') => {
  navigator.clipboard.writeText(text).then(() => {
    toast.success(`${label} in Zwischenablage kopiert`);
  }).catch(err => {
    console.error('Copy failed:', err);
    toast.error('Kopieren fehlgeschlagen');
  });
};

/**
 * Escape a value for safe inclusion in a CSV cell.
 */
export const escapeCsvCell = (value) => {
  if (value === null || value === undefined) {
    return '""';
  }
  const str = String(value);
  if (str.includes(',') || str.includes('\n') || str.includes('"') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

/**
 * Validate citations against a test configuration.
 */
export const validateCitations = (citations, testConfig) => {
  const issues = [];
  const citationUrls = citations.map(c => c.url || '').filter(Boolean);

  const uniqueUrls = [...new Set(citationUrls)];
  if (uniqueUrls.length !== citationUrls.length) {
    issues.push('Duplicate citations found');
  }

  if (uniqueUrls.length > 2) {
    issues.push(`Too many citations: ${uniqueUrls.length} (max 2)`);
  }

  const forbiddenFound = uniqueUrls.filter(url =>
    testConfig.forbiddenPaths.some(forbidden => url.toLowerCase().includes(forbidden.toLowerCase()))
  );
  if (forbiddenFound.length > 0) {
    issues.push(`Forbidden paths found: ${forbiddenFound.join(', ')}`);
  }

  if (testConfig.expectedCanonicalPaths && testConfig.expectedCanonicalPaths.length > 0) {
    const hasCanonical = uniqueUrls.some(url =>
      testConfig.expectedCanonicalPaths.some(canonical => url.toLowerCase().includes(canonical.toLowerCase()))
    );
    if (!hasCanonical) {
      issues.push(`No canonical path found. Expected one of: ${testConfig.expectedCanonicalPaths.join(', ')}`);
    }
  }

  return {
    passed: issues.length === 0,
    issues,
    citations: citations.slice(0, 2).map(c => ({ title: c.title, url: c.url }))
  };
};

/**
 * Estimate test count based on suite configuration.
 */
export const estimateTestCount = (testSuiteForm, INTENT_CATEGORIES) => {
  const suite = testSuiteForm.suite;
  const limit = testSuiteForm.limit;

  let baseCount = 0;
  if (suite === 'full') {
    baseCount = 240 * 4;
    if (testSuiteForm.include_redteam) {
      baseCount += 40 * 4;
    }
    if (testSuiteForm.include_dialogues) {
      baseCount += 30;
    }
  } else if (suite === 'golden') {
    baseCount = 16;
  } else if (suite === 'smoke') {
    baseCount = 20;
  } else if (suite === 'fast') {
    baseCount = 4;
  }

  if (testSuiteForm.intent_tags.length > 0 && testSuiteForm.intent_tags.length < INTENT_CATEGORIES.length) {
    baseCount = Math.round(baseCount * (testSuiteForm.intent_tags.length / INTENT_CATEGORIES.length));
  }

  if (testSuiteForm.suite_tags.includes('P0_core')) {
    baseCount = Math.round(baseCount * 0.2);
  }

  if (limit && limit > 0) {
    baseCount = Math.min(baseCount, limit);
  }

  return baseCount;
};
